import { ComBox } from "@/data/models/ComBox";
import { IresDevice } from "@/data/models/IresDevice";
import { Panel } from "@/data/models/Panel";

/**
 * What the device detail page has to say about a serial number before it can show a device.
 *
 * - `MISSING`: there is no device record at all, e.g. a mistyped serial in the IRES device search.
 * - `NEVER_REPORTED`: the record exists but the device has never reported. The production
 *   provisioning (the Raspberry Pi, `createdBy: "RaspberryProvisioning"`) creates a ComBox record
 *   with only `serialNo`, `createdAt`, `createdBy` and `dateOfManufacture`; the status block is
 *   written by IRES Cloud on the device's first contact. Without it the status cards have nothing
 *   to show, and the page rendered blank. A status block without `createdAt` counts as no status:
 *   see `getIresDeviceRecordState`.
 * - `REPORTED`: a normal device.
 */
export enum IresDeviceRecordState {
  MISSING = "MISSING",
  NEVER_REPORTED = "NEVER_REPORTED",
  REPORTED = "REPORTED",
}

/**
 * Classifies a device record. `null` means the record does not exist.
 *
 * The status block is the signal because it is what IRES Cloud writes when a device first reports,
 * and what every status display reads: `comBoxStatus` for a ComBox, `panelStatus` for a Panel.
 *
 * Only a status with `createdAt` counts. Several writers merge a single field into the status
 * (`online`, a ping's `ipuStatus`, the power-fail marking), and a merge creates the record when
 * none exists. HE307 (2026-10-07) had a status with no `createdAt`, and the status card crashed
 * reading the last update from it. A real status report always carries `createdAt`.
 */
export function getIresDeviceRecordState(iresDevice: IresDevice | null): IresDeviceRecordState {
  if (!iresDevice) {
    return IresDeviceRecordState.MISSING;
  }

  const hasStatus =
    !!(iresDevice as ComBox).comBoxStatus?.createdAt || !!(iresDevice as Panel).panelStatus?.createdAt;

  return hasStatus ? IresDeviceRecordState.REPORTED : IresDeviceRecordState.NEVER_REPORTED;
}
